import { type ReactNode, useState } from "react";
import type { RunGateResponse } from "@patent-studio/api-types";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { StageFooter, type FooterCondition } from "../../layout/StageFooter";
import { positionRowId } from "../anchors";
import { Unanswered } from "../Unanswered";

type CommitGate = RunGateResponse["commit"];

/** How many positions stand in the way, with the verb that agrees. */
function stillNeed(count: number): string {
  return count === 1
    ? "1 position still needs confirming"
    : `${count} positions still need confirming`;
}

/** The heading and the line beneath it, for the gate as the server answered it. */
function words(
  commit: CommitGate | undefined,
  failed: boolean,
): { heading: string; detail: string } {
  if (failed) {
    return {
      heading: "The gate could not be checked",
      detail:
        "Confirm, amend or remove the remaining positions before continuing.",
    };
  }
  if (commit?.ready === true) {
    return {
      heading: "Ready for Claim Strategy",
      detail: "The confirmed positions carry forward into Claim Strategy.",
    };
  }
  if (commit !== undefined && commit.outstanding === 0) {
    return {
      heading: "There are no positions to carry forward",
      detail:
        "Add a position, assess it and confirm it before continuing to Claim Strategy.",
    };
  }
  return {
    heading: "Review every position to continue",
    detail:
      commit?.outstanding === 1
        ? "Confirm, amend or remove the remaining position before continuing."
        : "Confirm, amend or remove the remaining positions before continuing.",
  };
}

/** The one condition the footer lists: the positions still to settle, jumping to the first. */
function conditionsOf(commit: CommitGate | undefined): FooterCondition[] {
  if (commit === undefined || commit.outstanding === 0) return [];
  return [
    {
      id: "settled",
      outstanding: true,
      first_id: commit.first_id,
      label: `Confirm all positions first: ${stillNeed(commit.outstanding)}.`,
    },
  ];
}

/**
 * The gate out of the analysis, as the server projects it: Continue to Claim
 * Strategy opens only when every position is settled, which is what the
 * commit act checks again. An out-of-date analysis is warned about before the
 * attorney is carried on. Until the server has answered, the gate is closed
 * and names nothing; a gate the server could not answer for says so and
 * offers to ask again.
 */
export function ContinueFooter({
  commit,
  stale,
  error,
  onRetry,
  onContinue,
  continuing,
  refusal,
  above,
}: {
  commit: CommitGate | undefined;
  stale: boolean;
  /** Why the gate could not be read, or nothing while it could. */
  error: unknown;
  onRetry: () => void;
  onContinue: () => void;
  /** The commit is in flight. */
  continuing: boolean;
  /** What the server said when it refused the last commit, or nothing. */
  refusal: string | null;
  /** Kept in view above the bar: the banner over the page's suggested changes. */
  above?: ReactNode;
}) {
  const [warning, setWarning] = useState(false);
  const failed = error !== null && error !== undefined;
  const ready = !failed && (commit?.ready ?? false);
  const { heading, detail } = words(commit, failed);
  return (
    <>
      <StageFooter
        testId="novelty-continue-footer"
        heading={heading}
        detail={detail}
        ready={ready}
        submitting={continuing}
        label="Continue to Claim Strategy"
        pendingLabel="Continuing…"
        onSubmit={() => {
          if (stale) {
            setWarning(true);
            return;
          }
          onContinue();
        }}
        sticky
        above={above}
        conditions={failed ? [] : conditionsOf(commit)}
        resolveTarget={(condition) =>
          condition.first_id === null ? null : positionRowId(condition.first_id)
        }
      >
        {failed && (
          <Unanswered
            what="Whether the analysis can be carried forward"
            error={error}
            onRetry={onRetry}
          />
        )}
        {refusal !== null && (
          <p role="alert" className="mt-2 text-[11.5px] text-destructive">
            {refusal}
          </p>
        )}
      </StageFooter>
      <ConfirmDialog
        open={warning}
        title="Continue with an out-of-date analysis?"
        description="The results you are carrying forward do not reflect the current library and positions. Re-run the analysis first, or continue with the verdicts as they stand."
        actionLabel="Continue anyway"
        onCancel={() => setWarning(false)}
        onConfirm={() => {
          setWarning(false);
          onContinue();
        }}
      />
    </>
  );
}
